import { Controller } from "@hotwired/stimulus";

export default class CountdownController extends Controller {
  static targets = ["output"];

  static values = {
    startTime: String,
    endTime: String,
    url: String,
  };

  connect() {
    this.startTime = new Date(this.startTimeValue);
    this.endTime = new Date(this.endTimeValue);
    this.apiCalled = false;
    this.handleLanguageChange = () => this.tick();
    document.addEventListener(
      "itoons:language-change",
      this.handleLanguageChange,
    );

    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnect() {
    clearInterval(this.interval);
    document.removeEventListener(
      "itoons:language-change",
      this.handleLanguageChange,
    );
  }

  async callApi() {
    if (!this.hasUrlValue || this.apiCalled) return;

    this.apiCalled = true;

    const turboRequest = (await import("../helpers/api")).turboRequest;

    await turboRequest(this.urlValue, {
      method: "GET",
      turboStream: true,
    });
  }

  tick() {
    const now = new Date();
    if (now < this.startTime) {
      this.outputTarget.textContent = "Countdown not started";
      return;
    }
    if (now >= this.endTime) {
      clearInterval(this.interval);
      this.outputTarget.textContent =
        this.outputTarget.dataset.text || "Countdown ended";
      this.callApi();
      return;
    }

    const diff = this.endTime - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    const compactFormat = this.element.dataset.compactFormat;

    const parts = [];

    if (days > 0) {
      parts.push(
        compactFormat
          ? this.formatUnit(days, "day", true)
          : this.formatUnit(days, "day"),
      );
    }

    if (hours > 0) {
      parts.push(
        compactFormat
          ? this.formatUnit(hours, "hour", true)
          : this.formatUnit(hours, "hour"),
      );
    }

    if (minutes > 0) {
      parts.push(
        compactFormat
          ? this.formatUnit(minutes, "minute", true)
          : this.formatUnit(minutes, "minute"),
      );
    }

    parts.push(
      compactFormat
        ? this.formatUnit(seconds, "second", true)
        : this.formatUnit(seconds, "second"),
    );

    this.outputTarget.textContent = parts.join(" ");
  }

  formatUnit(value, unit, compact = false) {
    try {
      return new Intl.NumberFormat(this.language, {
        style: "unit",
        unit,
        unitDisplay: compact ? "narrow" : "long",
      }).format(value);
    } catch {
      return new Intl.NumberFormat("en", {
        style: "unit",
        unit,
        unitDisplay: compact ? "narrow" : "long",
      }).format(value);
    }
  }

  get language() {
    return globalThis.localStorage.getItem("selected_lang") || "en";
  }
}
